import React, { useEffect, useRef, useState } from 'react';
import { offerData } from '../data/mock';
import { Hammer, Rocket, Wrench, ArrowRight } from 'lucide-react';

const iconMap = {
  Hammer,
  Rocket,
  Wrench
};

const columnColors = [
  { accent: '#F08A24', light: '#FFF5E6' },  // Orange - Build
  { accent: '#1F4E79', light: '#E6EEF5' },  // Blue - Scale
  { accent: '#2D8C4E', light: '#E6F5EC' },  // Green - Rescue
];

const OfferSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.15 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const radiuses = [
    '255px 15px 225px 15px / 15px 225px 15px 255px',
    '15px 225px 15px 255px / 255px 15px 225px 15px',
    '225px 15px 255px 15px / 15px 255px 15px 225px'
  ];

  return (
    <section
      id="offer"
      ref={sectionRef}
      className="min-h-screen px-6 py-20 md:pl-24"
    >
      <div className="max-w-6xl mx-auto">
        
        {/* Section Header */}
        <div 
          className={`text-center mb-16 transition-all duration-700
            ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}
          `}
        >
          <span className="font-architect text-lg text-[#F08A24] mb-2 block">
            {offerData.subtitle}
          </span>
          <h2 className="font-marker text-3xl md:text-4xl lg:text-5xl text-[#2D2D2D]">
            {offerData.title}
          </h2>
        </div>

        {/* Offer Columns */}
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {offerData.blocks.map((block, index) => {
            const IconComponent = iconMap[block.icon];
            const color = columnColors[index];
            
            return (
              <div
                key={index}
                className={`bg-[#FDFBF7] border-[3px] border-[#2D2D2D] overflow-hidden
                  transition-all duration-500 cursor-default group
                  hover:-translate-y-2
                  ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'}
                `}
                style={{ 
                  borderRadius: radiuses[index],
                  boxShadow: '4px 4px 0px #2D2D2D',
                  transitionDelay: `${index * 0.2}s`
                }}
              >
                {/* Header with number */}
                <div 
                  className="p-6 pb-4"
                  style={{ backgroundColor: color.light }}
                >
                  <div className="flex items-center justify-between mb-4">
                    <span 
                      className="font-marker text-4xl"
                      style={{ color: color.accent }}
                    >
                      {block.number}
                    </span>
                    <div 
                      className="p-3 border-2 border-[#2D2D2D] transition-transform group-hover:rotate-12"
                      style={{ 
                        backgroundColor: color.accent,
                        borderRadius: '255px 15px 225px 15px / 15px 225px 15px 255px'
                      }}
                    >
                      <IconComponent className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <h3 className="font-marker text-2xl md:text-3xl text-[#2D2D2D]">
                    {block.name}
                  </h3>
                  <p className="font-patrick text-base text-[#2D2D2D]/70 mt-1">
                    {block.subtitle}
                  </p>
                </div>

                {/* Content */}
                <div className="p-6 pt-4">
                  {/* Objective tag */}
                  <div 
                    className="inline-block px-4 py-2 mb-4 border-2 border-[#2D2D2D]"
                    style={{ 
                      backgroundColor: color.light,
                      borderRadius: '255px 15px 225px 15px / 15px 225px 15px 255px'
                    }}
                  >
                    <span className="font-patrick text-sm" style={{ color: color.accent }}>
                      {block.objective}
                    </span>
                  </div>

                  {/* Description */}
                  <p className="font-architect text-base text-[#2D2D2D]/85 leading-relaxed">
                    {block.description}
                  </p>

                  {/* Learn more link */}
                  <button 
                    onClick={() => {
                      const element = document.getElementById('contact');
                      if (element) element.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="mt-6 flex items-center gap-2 font-patrick text-base
                      transition-all hover:gap-3"
                    style={{ color: color.accent }}
                  >
                    <span>En savoir plus</span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

      </div>
    </section>
  );
};

export default OfferSection;
